import { useState } from 'react';
import { 
  X, 
  FileText, 
  Layers, 
  Target, 
  BarChart, 
  PlayCircle,
  Users,
  HelpCircle,
  Lightbulb,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  TrendingUp,
  Clock,
  DollarSign,
  MessageSquare,
  AlertCircle,
  Star,
} from 'lucide-react';
import GenerationProgress from './GenerationProgress';
import { DocumentContent } from './DocumentViewer';
import { generateManagedDocumentWithAI } from '../services/openaiService';
import { QUALITY_LEVELS, type QualityLevel, type DocumentTypeDefinition } from '../data/gestionDocumentaireTypes';
import { getColorClasses } from '../utils/colorUtils';

interface DocumentInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: DocumentTypeDefinition;
  contentConfig: {
    esprit: {
      heroTitle: string;
      heroDescription: string;
      pourquoi: { probleme: string; solution: string };
      analogie?: { title: string; content: string; points: string[]; conclusion: string };
      piliers?: Array<{ icon: any; title: string; desc: string; color: string }>;
      prismeAdvantages: Array<{ title: string; desc: string }>;
    };
    qui: {
      heroTitle: string;
      heroDescription: string;
      profils: Array<{
        title: string;
        icon: any;
        color: string;
        scenarios: string[];
        niveau: string;
      }>;
      personas?: Array<{ role: string; focus: string; icon: any }>;
    };
    quand: {
      heroTitle: string;
      heroDescription: string;
      questions: Array<{ question: string; siNon: string }>;
      signaux?: string[];
      timing?: Array<{ timing: string; raison: string }>;
    };
    valeur: {
      heroTitle: string;
      heroDescription: string;
      benefices: Array<{ benefice: string; detail: string; icon: any; color: string }>;
      roi?: { title: string; content: string; points: Array<{ label: string; value: string }>; cout: string; valeur: string };
      arguments?: Array<{ persona: string; arguments: string[] }>;
    };
    niveaux: {
      heroTitle: string;
      heroDescription: string;
      comparisonTable?: Array<{ critere: string; values: string[] }>;
      niveauDetails: Array<{
        inclus: string[];
        quandUtiliser: string;
      }>;
    };
  };
  mockContext?: string;
}

export default function DocumentInfoModal({
  isOpen,
  onClose,
  documentType,
  contentConfig,
  mockContext,
}: DocumentInfoModalProps) {
  const [activeTab, setActiveTab] = useState<'esprit' | 'qui' | 'quand' | 'valeur' | 'niveaux' | 'simulation'>('esprit');
  const [isGeneratingExample, setIsGeneratingExample] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  const [exampleContent, setExampleContent] = useState<string | null>(null);
  const [simulationLevel, setSimulationLevel] = useState<QualityLevel>(3);

  if (!isOpen) return null;

  const handleGenerateExample = async () => {
    setIsGeneratingExample(true);
    setExampleContent(null);
    setGenerationProgress(0);
    setGenerationStep('');

    try {
      const levelDef = QUALITY_LEVELS.find((l) => l.level === simulationLevel);

      const steps = [
        { progress: 10, text: 'Analyse de la prise de besoin...', delay: 1000 },
        { progress: 30, text: 'Construction de la structure...', delay: 1600 },
        { progress: 50, text: 'Intégration des éléments clés...', delay: 2000 },
        { progress: 75, text: 'Finalisation et cohérence...', delay: 2200 },
      ];

      let stepIndex = 0;
      const interval = setInterval(() => {
        if (stepIndex < steps.length) {
          const step = steps[stepIndex];
          setGenerationStep(step.text);
          setGenerationProgress(step.progress);
          stepIndex++;
        }
      }, 2000);

      const context = mockContext || `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.`;

      const result = await generateManagedDocumentWithAI({
        documentTypeCode: documentType.code,
        documentTypeTitle: documentType.title,
        qualityLevel: simulationLevel,
        qualityLabel: levelDef?.label || `Niveau ${simulationLevel}`,
        frameworks: documentType.frameworks,
        context: context,
      });

      clearInterval(interval);
      setGenerationProgress(100);
      setGenerationStep('Génération terminée !');
      await new Promise((resolve) => setTimeout(resolve, 800));

      setExampleContent(result.content);
      setIsGeneratingExample(false);
    } catch (error) {
      console.error('Erreur simulation:', error);
      alert(
        '❌ Erreur lors de la simulation.\n\nVérifiez que la clé OpenAI est configurée côté serveur (OPENAI_API_KEY) et regardez la console (F12) pour plus de détails.'
      );
      setIsGeneratingExample(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  if (isGeneratingExample && !exampleContent) {
    return (
      <GenerationProgress
        currentStep={generationStep || 'Préparation...'}
        progress={generationProgress}
        documentLabels={{
          doc1: `${documentType.title} – Exemple (Niveau ${simulationLevel})`,
          doc2: 'Document généré',
        }}
      />
    );
  }

  const colorMap: Record<string, string> = {
    blue: 'from-blue-600 to-cyan-600',
    indigo: 'from-indigo-600 via-purple-600 to-pink-600',
    purple: 'from-purple-600 to-pink-600',
    orange: 'from-orange-600 to-red-600',
    emerald: 'from-emerald-600 to-teal-600',
    red: 'from-red-600 to-rose-600',
  };

  const gradientClass = colorMap[documentType.color] || 'from-indigo-600 to-purple-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-[90vw] h-[90vh] max-w-[1600px] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${gradientClass} text-white px-8 py-6 flex items-center justify-between`}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center shadow-lg backdrop-blur-sm">
              <FileText className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">{documentType.title} – Guide Complet</h2>
              <p className="text-sm text-white/90">
                {documentType.description}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b-2 border-gray-200 px-8 pt-4 pb-3 bg-gradient-to-b from-gray-50 to-white overflow-x-auto">
          {[
            { id: 'esprit' as const, label: 'Esprit & Pourquoi', icon: Lightbulb },
            { id: 'qui' as const, label: 'À qui livrer?', icon: Users },
            { id: 'quand' as const, label: 'Quand & Pourquoi?', icon: HelpCircle },
            { id: 'valeur' as const, label: 'Valeur Client', icon: TrendingUp },
            { id: 'niveaux' as const, label: 'Les 5 Niveaux', icon: Layers },
            { id: 'simulation' as const, label: 'Simulation', icon: PlayCircle },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          {/* ONGLET 1: ESPRIT & POURQUOI */}
          {activeTab === 'esprit' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className={`bg-gradient-to-br ${gradientClass} rounded-2xl p-8 text-white shadow-xl`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Lightbulb className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">{contentConfig.esprit.heroTitle}</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      {contentConfig.esprit.heroDescription}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border-2 border-indigo-100 p-6 shadow-lg">
                <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                  <Target className="w-6 h-6 text-indigo-600" />
                  Pourquoi ce document existe-t-il?
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-indigo-600" />
                      <h5 className="font-semibold text-gray-900">Le Problème</h5>
                    </div>
                    <p className="text-sm text-gray-700">{contentConfig.esprit.pourquoi.probleme}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      <h5 className="font-semibold text-gray-900">La Solution</h5>
                    </div>
                    <p className="text-sm text-gray-700">{contentConfig.esprit.pourquoi.solution}</p>
                  </div>
                </div>
              </div>

              {contentConfig.esprit.analogie && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                  <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    {contentConfig.esprit.analogie.title}
                  </h4>
                  <div className="bg-purple-50 rounded-lg p-5 border-l-4 border-purple-500">
                    <p className="text-gray-800 leading-relaxed">{contentConfig.esprit.analogie.content}</p>
                    {contentConfig.esprit.analogie.points && (
                      <ul className="list-disc list-inside mt-3 space-y-2 text-gray-700">
                        {contentConfig.esprit.analogie.points.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    )}
                    <p className="mt-3 text-gray-800">{contentConfig.esprit.analogie.conclusion}</p>
                  </div>
                </div>
              )}

              {contentConfig.esprit.piliers && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                  <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-4">
                    <Layers className="w-5 h-5 text-indigo-600" />
                    Les Piliers Fondamentaux
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {contentConfig.esprit.piliers.map((pilier, idx) => {
                      const colors = getColorClasses(pilier.color);
                      return (
                        <div key={idx} className={`${colors.bg} rounded-lg p-4 border ${colors.border}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <pilier.icon className={`w-5 h-5 ${colors.text}`} />
                            <h5 className="font-semibold text-gray-900 text-sm">{pilier.title}</h5>
                          </div>
                          <p className="text-xs text-gray-700">{pilier.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Pourquoi PRIsME excelle dans ce livrable
                </h4>
                <div className="space-y-3">
                  {contentConfig.esprit.prismeAdvantages.map((adv, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">{adv.title}</p>
                        <p className="text-sm text-gray-700">{adv.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ONGLET 2: À QUI LIVRER */}
          {activeTab === 'qui' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className={`bg-gradient-to-br ${colorMap.blue || 'from-blue-600 to-cyan-600'} rounded-2xl p-8 text-white shadow-xl`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Users className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">{contentConfig.qui.heroTitle}</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      {contentConfig.qui.heroDescription}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contentConfig.qui.profils.map((profile, idx) => {
                  const colors = getColorClasses(profile.color);
                  return (
                    <div key={idx} className={`bg-white rounded-xl border-2 ${colors.border} p-6 shadow-lg`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center`}>
                          <profile.icon className={`w-6 h-6 ${colors.text}`} />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-gray-900">{profile.title}</h4>
                          <p className={`text-xs font-semibold ${colors.text}`}>{profile.niveau}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Cas d'usage typiques:</p>
                        {profile.scenarios.map((scenario, sIdx) => (
                          <div key={sIdx} className="flex items-start gap-2">
                            <CheckCircle2 className={`w-4 h-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                            <p className="text-sm text-gray-700">{scenario}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {contentConfig.qui.personas && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                  <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                    <MessageSquare className="w-6 h-6 text-blue-600" />
                    Personas: Qui lit ce document?
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {contentConfig.qui.personas.map((persona, idx) => (
                      <div key={idx} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <persona.icon className="w-5 h-5 text-blue-600" />
                          <h5 className="font-semibold text-gray-900 text-sm">{persona.role}</h5>
                        </div>
                        <p className="text-xs text-gray-700">{persona.focus}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ONGLET 3: QUAND & POURQUOI */}
          {activeTab === 'quand' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className={`bg-gradient-to-br ${colorMap.purple || 'from-purple-600 to-pink-600'} rounded-2xl p-8 text-white shadow-xl`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <HelpCircle className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">{contentConfig.quand.heroTitle}</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      {contentConfig.quand.heroDescription}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border-2 border-purple-100 p-6 shadow-lg">
                <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                  <MessageSquare className="w-6 h-6 text-purple-600" />
                  Questions à poser au client
                </h4>
                <div className="space-y-4">
                  {contentConfig.quand.questions && contentConfig.quand.questions.map((item, idx) => (
                    <div key={idx} className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-500">
                      <p className="font-semibold text-gray-900 mb-1">{item.question}</p>
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-purple-600" />
                        {item.siNon}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {contentConfig.quand.signaux && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                  <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-4">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    Signaux d'alarme: Le client a BESOIN de ce document si...
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {contentConfig.quand.signaux.map((signal, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-orange-50 rounded-lg p-3 border border-orange-200">
                        <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-800">{signal}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {contentConfig.quand.timing && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 p-6 shadow-md">
                  <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3">
                    <Clock className="w-5 h-5 text-indigo-600" />
                    Timing idéal pour livrer ce document
                  </h4>
                  <div className="space-y-3">
                    {contentConfig.quand.timing.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-white rounded-lg p-3">
                        <Clock className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900">{item.timing}</p>
                          <p className="text-sm text-gray-700">{item.raison}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ONGLET 4: VALEUR CLIENT */}
          {activeTab === 'valeur' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className={`bg-gradient-to-br ${colorMap.emerald || 'from-emerald-600 to-teal-600'} rounded-2xl p-8 text-white shadow-xl`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <TrendingUp className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">{contentConfig.valeur.heroTitle}</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      {contentConfig.valeur.heroDescription}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border-2 border-emerald-100 p-6 shadow-lg">
                <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  Bénéfices concrets pour le client
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contentConfig.valeur.benefices.map((benefice, idx) => {
                    const colors = getColorClasses(benefice.color);
                    return (
                      <div key={idx} className={`${colors.bg} rounded-lg p-4 border ${colors.border}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <benefice.icon className={`w-5 h-5 ${colors.text}`} />
                          <h5 className="font-semibold text-gray-900">{benefice.benefice}</h5>
                        </div>
                        <p className="text-sm text-gray-700">{benefice.detail}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {contentConfig.valeur.roi && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                  <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-4">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    {contentConfig.valeur.roi.title}
                  </h4>
                  <div className="bg-emerald-50 rounded-lg p-5 border-l-4 border-emerald-500">
                    <p className="text-gray-800 leading-relaxed mb-3">{contentConfig.valeur.roi.content}</p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      {contentConfig.valeur.roi.points.map((point, idx) => (
                        <li key={idx}><strong>{point.label}:</strong> {point.value}</li>
                      ))}
                    </ul>
                    <p className="mt-3 text-gray-800">
                      <strong>{contentConfig.valeur.roi.cout}</strong><br />
                      <strong>{contentConfig.valeur.roi.valeur}</strong>
                    </p>
                  </div>
                </div>
              )}

              {contentConfig.valeur.arguments && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 p-6 shadow-md">
                  <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3">
                    <MessageSquare className="w-5 h-5 text-indigo-600" />
                    Arguments de vente par persona
                  </h4>
                  <div className="space-y-4">
                    {contentConfig.valeur.arguments.map((item, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                        <h5 className="font-bold text-gray-900 mb-2">{item.persona}</h5>
                        <ul className="space-y-1">
                          {item.arguments.map((arg, aIdx) => (
                            <li key={aIdx} className="flex items-start gap-2 text-sm text-gray-700">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                              {arg}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ONGLET 5: LES 5 NIVEAUX */}
          {activeTab === 'niveaux' && (
            <div className="max-w-6xl mx-auto space-y-6">
              <div className={`bg-gradient-to-br ${colorMap.orange || 'from-orange-600 to-red-600'} rounded-2xl p-8 text-white shadow-xl`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Layers className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">{contentConfig.niveaux.heroTitle}</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      {contentConfig.niveaux.heroDescription}
                    </p>
                  </div>
                </div>
              </div>

              {contentConfig.niveaux.comparisonTable && (
                <div className="bg-white rounded-xl border-2 border-orange-100 p-6 shadow-lg">
                  <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                    <BarChart className="w-6 h-6 text-orange-600" />
                    Comparaison des 5 niveaux
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-200 px-4 py-3 text-left text-sm font-bold text-gray-900">Critère</th>
                          {QUALITY_LEVELS.map((level) => (
                            <th key={level.level} className="border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-900">
                              Niveau {level.level}<br />
                              <span className="text-xs font-normal text-gray-600">{level.shortLabel}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {contentConfig.niveaux.comparisonTable.map((row, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-900">{row.critere}</td>
                            {row.values.map((value, vIdx) => (
                              <td key={vIdx} className="border border-gray-200 px-4 py-3 text-sm text-center text-gray-700">{value}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {QUALITY_LEVELS.map((level, idx) => (
                  <div key={idx} className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-md">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-br from-${['indigo', 'blue', 'purple', 'orange', 'red'][idx]}-500 to-${['purple', 'cyan', 'pink', 'amber', 'rose'][idx]}-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg`}>
                        {level.level}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900">{level.label}</h4>
                        <p className="text-sm text-gray-600">{level.shortLabel}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          Inclus dans ce niveau:
                        </h5>
                        <ul className="space-y-1 text-sm text-gray-700">
                          {contentConfig.niveaux.niveauDetails[idx]?.inclus.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Target className="w-4 h-4 text-indigo-600" />
                          Quand utiliser ce niveau:
                        </h5>
                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                          {contentConfig.niveaux.niveauDetails[idx]?.quandUtiliser}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ONGLET 6: SIMULATION */}
          {activeTab === 'simulation' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-lg flex items-start gap-4">
                <div className="mt-1">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                    <PlayCircle className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Simulation avec données mock
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Cette simulation utilise le même moteur OpenAI que PRIsME utilisera pour générer un vrai
                    document pour un client. On injecte une prise de besoin &quot;mock&quot; et on génère un document selon le <strong>niveau de livrable</strong> choisi ci-dessous.
                  </p>

                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-700 font-semibold">Niveau de simulation:</span>
                    {QUALITY_LEVELS.map((lvl) => (
                      <button
                        key={lvl.level}
                        onClick={() => setSimulationLevel(lvl.level)}
                        className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-all ${
                          simulationLevel === lvl.level
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg scale-105'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <span className="font-bold mr-1">{lvl.level}</span>
                        <span>{lvl.shortLabel}</span>
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={handleGenerateExample}
                    disabled={isGeneratingExample}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
                  >
                    <PlayCircle className="w-5 h-5" />
                    {isGeneratingExample
                      ? 'Simulation en cours...'
                      : `Lancer la simulation – Niveau ${simulationLevel}`}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Note: nécessite que la clé OpenAI soit configurée côté serveur. La génération peut prendre
                    quelques secondes.
                  </p>
                </div>
              </div>

              {exampleContent && (
                <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-lg">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">
                    Exemple de document généré (mock) – Vue PRIsME
                  </h4>
                  <div className="border border-gray-200 rounded-lg max-h-[60vh] overflow-y-auto bg-gray-50 px-6 py-4">
                    <DocumentContent content={exampleContent} color={documentType.color as any} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

